var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import styled, { spacing } from "@rentiohq/web-theme";
import { rem } from "polished";
/**
 * The shell both income tables wear: the read-only one describing the standard
 * and the editable one underneath Personalize.
 *
 * Exported so the two cannot drift apart. They show the same six rows and mean
 * the same thing, so a broker pressing Personalize should see the table they
 * were just reading, with fields where the numbers were.
 */
// Room for the field plus "and below" / "and above" on the outermost rows.
var RENT_WIDTH = "200px";
var RENT_FIELD_WIDTH = "120px";
var RATIO_WIDTH = "68px";
var ROW_HEIGHT = "48px";
export var Table = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  border: 1px solid ", ";\n  border-radius: ", ";\n  overflow: hidden;\n"], ["\n  border: 1px solid ", ";\n  border-radius: ", ";\n  overflow: hidden;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.inputStyles.borderColor.base;
}, function (_a) {
    var theme = _a.theme;
    return theme.borderRadius();
});
var row = "\n  display: flex;\n  align-items: center;\n  gap: ".concat(spacing("tight"), ";\n");
export var HeadRow = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", ";\n  font-size: ", ";\n  font-weight: 600;\n  color: ", ";\n  border-bottom: 1px solid ", ";\n"], ["\n  ", ";\n  font-size: ", ";\n  font-weight: 600;\n  color: ", ";\n  border-bottom: 1px solid ", ";\n"])), row, function (_a) {
    var theme = _a.theme;
    return rem("14px", theme.baseFontSize);
}, function (_a) {
    var theme = _a.theme;
    return theme.getColor("gray", 600);
}, function (_a) {
    var theme = _a.theme;
    return theme.getColor("gray", 200);
});
/**
 * Banded rows, so a long line of numbers stays readable across, and as tall
 * with text in them as with fields: the read-only table and the editable one
 * show the same six rows, so pressing Personalize should not make the page
 * jump.
 */
export var Row = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", ";\n  min-height: ", ";\n  background: ", ";\n"], ["\n  ", ";\n  min-height: ", ";\n  background: ", ";\n"])), row, ROW_HEIGHT, function (_a) {
    var theme = _a.theme, $isOdd = _a.$isOdd;
    return $isOdd ? theme.getColor("gray", 0) : theme.getColor("white");
});
export var RentCell = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  width: ", ";\n  flex-shrink: 0;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  padding: ", " 0\n    ", "\n    ", ";\n"], ["\n  width: ", ";\n  flex-shrink: 0;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  padding: ", " 0\n    ", "\n    ", ";\n"])), RENT_WIDTH, function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
}, function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
}, function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
}, function (_a) {
    var theme = _a.theme;
    return theme.spacing("base");
});
/**
 * Ringed red when this rent does not climb past the row above it, which is the
 * one thing here that refuses a save. The message underneath says what is wrong
 * and this says where, so nobody has to count rows to find it.
 */
export var RentField = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  width: ", ";\n  flex-shrink: 0;\n  border-radius: ", ";\n  ", ";\n"], ["\n  width: ", ";\n  flex-shrink: 0;\n  border-radius: ", ";\n  ", ";\n"])), RENT_FIELD_WIDTH, function (_a) {
    var theme = _a.theme;
    return theme.borderRadius();
}, function (_a) {
    var $hasError = _a.$hasError, theme = _a.theme;
    return $hasError
        ? "box-shadow: 0 0 0 2px ".concat(theme.inputStyles.borderColor.error, ";")
        : "";
});
export var ScoreCell = styled.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  flex: 1;\n  min-width: 0;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  padding: ", "\n    ", ";\n"], ["\n  flex: 1;\n  min-width: 0;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  padding: ", "\n    ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
}, function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
}, function (_a) {
    var theme = _a.theme;
    return theme.spacing("base");
});
/**
 * Ringed when this row asks more than the cheaper one above it, so the warning
 * below the table points at something instead of making the broker hunt. A ring
 * around the cell rather than a red field: nothing is refused here.
 */
export var RatioField = styled.div(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  width: ", ";\n  flex-shrink: 0;\n  border-radius: ", ";\n  ", ";\n"], ["\n  width: ", ";\n  flex-shrink: 0;\n  border-radius: ", ";\n  ", ";\n"])), RATIO_WIDTH, function (_a) {
    var theme = _a.theme;
    return theme.borderRadius();
}, function (_a) {
    var $isWarning = _a.$isWarning, theme = _a.theme;
    return $isWarning
        ? "box-shadow: 0 0 0 2px ".concat(theme.getColor("orange", 1000), ";")
        : "";
});
export var Text = styled.span(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n  flex-shrink: 0;\n  color: ", ";\n"], ["\n  flex-shrink: 0;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.getColor("gray", 800);
});
/** What the multiple works out to at that rent, which is what a broker thinks in. */
export var Amount = styled.span(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n  color: ", ";\n"], ["\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.getColor("gray", 400);
});
export var Strong = styled.span(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n  font-weight: 600;\n  color: ", ";\n"], ["\n  font-weight: 600;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.getColor("gray", 1000);
});
/**
 * One worked example says more than a paragraph about interpolation.
 *
 * Under the table rather than in it: it describes the whole table and is not a
 * row of it, and a bordered strip at the bottom read as one.
 */
export var Note = styled.div(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n  margin-top: ", ";\n  font-size: ", ";\n  color: ", ";\n"], ["\n  margin-top: ", ";\n  font-size: ", ";\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
}, function (_a) {
    var theme = _a.theme;
    return rem("14px", theme.baseFontSize);
}, function (_a) {
    var theme = _a.theme;
    return theme.getColor("gray", 400);
});
/**
 * Carries on the banding of the rows above it rather than starting a new block,
 * so the table reads as one thing with a way to grow.
 */
export var AddRow = styled.div(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  min-height: ", ";\n  padding: 0 ", ";\n  background: ", ";\n"], ["\n  display: flex;\n  align-items: center;\n  min-height: ", ";\n  padding: 0 ", ";\n  background: ", ";\n"])), ROW_HEIGHT, function (_a) {
    var theme = _a.theme;
    return theme.spacing("base");
}, function (_a) {
    var theme = _a.theme, $isOdd = _a.$isOdd;
    return $isOdd ? theme.getColor("gray", 0) : theme.getColor("white");
});
/** Only where a row may go, so the rows above it keep their alignment. */
export var RemoveCell = styled.div(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n  width: 48px;\n  flex-shrink: 0;\n  display: flex;\n  justify-content: center;\n  padding-right: ", ";\n"], ["\n  width: 48px;\n  flex-shrink: 0;\n  display: flex;\n  justify-content: center;\n  padding-right: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
});
export var Actions = styled.div(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  margin-top: ", ";\n"], ["\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  margin-top: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("base");
}, function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
});
export var Warning = styled.div(templateObject_15 || (templateObject_15 = __makeTemplateObject(["\n  margin-top: ", ";\n"], ["\n  margin-top: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("tight");
});
/** The icon and the words as one line, so they do not drift apart on wrap. */
export var AddLabel = styled.span(templateObject_16 || (templateObject_16 = __makeTemplateObject(["\n  display: inline-flex;\n  align-items: center;\n  gap: ", ";\n"], ["\n  display: inline-flex;\n  align-items: center;\n  gap: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("extraTight");
});
/**
 * The icon beside the sentence rather than above it, aligned to the first line
 * so a wrapping explanation stays readable.
 */
export var NoteLabel = styled.span(templateObject_17 || (templateObject_17 = __makeTemplateObject(["\n  display: flex;\n  align-items: flex-start;\n  gap: ", ";\n"], ["\n  display: flex;\n  align-items: flex-start;\n  gap: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing("extraTight");
});
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14, templateObject_15, templateObject_16, templateObject_17;
